import type { Due, Item, List, ListKind } from "./api.js";
import type { Repeat } from "./recurrence.js";

export interface ExchangeItem {
  readonly text: string;
  readonly quantity: string | null;
  /** Version 2 (g79). A version 1 document imports with none. */
  readonly category: string | null;
  readonly note: string | null;
  readonly due: Readonly<Due> | null;
  readonly done: boolean;
  readonly repeat: Repeat | null;
  readonly repeatAnchor: string | null;
}

export interface Exchange {
  readonly name: string;
  readonly kind: ListKind;
  readonly items: readonly ExchangeItem[];
}

/** A readable checklist. Full YAML remains the only import format. */
export function emitSimple(snapshot: Exchange, includeCompleted = false): string {
  const lines = [snapshot.name];
  for (const item of snapshot.items) {
    if (!includeCompleted && item.done) continue;
    lines.push(`- [${item.done ? "x" : " "}] ${item.text}${item.quantity ? ` (${item.quantity})` : ""}`
      + (item.category ? ` \u00b7 ${item.category}` : ""));
  }
  return lines.join("\n") + "\n";
}

/** Copy content explicitly: store objects and private bookkeeping never leave the app. */
export function captureExchange(list: List, items: readonly Item[]): Exchange {
  return {
    name: list.name, kind: list.kind,
    items: items.map(item => ({
      text: item.text, quantity: item.quantity, category: item.category ?? null, note: item.note,
      due: item.due ? { date: item.due.date, time: item.due.time, tz: item.due.tz } : null,
      done: item.done, repeat: item.repeat ?? null,
      repeatAnchor: item.repeat ? item.repeatAnchor ?? item.due!.date : null,
    })),
  };
}

/** JSON quoting fits our YAML subset; escape the remaining non-printable characters too. */
function scalar(value: string | null): string {
  return JSON.stringify(value).replace(/[\u007f-\u009f\u2028\u2029\ufffe\uffff]/g,
    char => `\\u${char.charCodeAt(0).toString(16).padStart(4, "0")}`);
}

/**
 * Version 2, defined in docs/list-exchange-format.md: version 1 with a category
 * after the quantity (g79). Import reads both. Sequence order is the caller's.
 */
export function emitExchange(snapshot: Exchange, includeCompleted = false): string {
  const items = snapshot.items.filter(item => includeCompleted || !item.done);
  const lines = ["version: 2", `name: ${scalar(snapshot.name)}`, `kind: ${scalar(snapshot.kind)}`,
    items.length ? "items:" : "items: []"];
  for (const item of items) {
    lines.push(`  - text: ${scalar(item.text)}`, `    quantity: ${scalar(item.quantity)}`,
      `    category: ${scalar(item.category)}`, `    note: ${scalar(item.note)}`);
    if (item.due) lines.push("    due:", `      date: ${scalar(item.due.date)}`,
      `      time: ${scalar(item.due.time)}`, `      tz: ${scalar(item.due.tz)}`);
    else lines.push("    due: null");
    lines.push(`    done: ${item.done}`, `    repeat: ${scalar(item.repeat)}`, `    repeatAnchor: ${scalar(item.repeatAnchor)}`);
  }
  return lines.join("\n") + "\n";
}
